// src/components/ClaimTimeline.js
//
// Record 690 (Bob 2026-10-14, suggestion 6): the claim's timeline - what has happened, where it is now, what comes next and
// by when. src\data\claimTimeline.js builds it from what the claim records; this only draws it. The member sees it open at
// the top of his claim; an officer finds it under "Timeline" (ClaimDetail).
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { buildTimeline } from '../data/claimTimeline';
import RulingRef from './RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer

const C = { line: '#2E4A6B', dim: '#8AA4BF', text: '#F4F6F8', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878' };

export default function ClaimTimeline({ claimId, claim, title, user }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!claimId) return undefined;
    // The same rows the History table reads (ruling 86). A refusal leaves the line to the claim's own dates.
    return onSnapshot(query(collection(db, 'claims', claimId, 'history'), orderBy('timestamp', 'desc'), limit(200)),
      (sn) => setRows(sn.docs.map((d) => d.data())), () => setRows([]));
  }, [claimId]);
  const t = useMemo(() => buildTimeline(claim, rows, Date.now()), [claim, rows]);
  if (!claim) return null;
  const leftWords = (n) => n < 0 ? (-n) + ' day' + (n === -1 ? '' : 's') + ' ago' : n === 0 ? 'today' : n === 1 ? 'tomorrow' : 'in ' + n + ' days';
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header"><div className="card-title">{title || 'Timeline'}</div></div>
      <div style={{ fontSize: 13, color: C.text, marginBottom: 10 }}><strong>Now:</strong> {t.now}</div>
      {t.past.length ? (
        <div style={{ borderLeft: '2px solid ' + C.line, marginLeft: 6, paddingLeft: 14 }}>
          {t.past.map((e, i) => (
            <div key={i} style={{ position: 'relative', marginBottom: 8 }}>
              <span style={{ position: 'absolute', left: -20, top: 5, width: 10, height: 10, borderRadius: 5, background: C.ok }} />
              <div style={{ fontSize: 11, color: C.dim }}>{e.date}</div>
              <div style={{ fontSize: 13, color: C.text }}>{e.what}</div>
            </div>
          ))}
        </div>
      ) : <div style={{ fontSize: 12, color: C.dim }}>No dated event is recorded on this claim yet.</div>}
      {t.next.length ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: C.dim, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>What comes next, and by when</div>
          {t.next.map((n, i) => (
            <div key={i} style={{ fontSize: 13, color: n.left < 0 ? C.bad : n.left <= 7 ? C.accent : C.text, marginBottom: 4 }}>
              {n.day} {n.date} ({leftWords(n.left)}) - {n.whose}: {n.what}
            </div>
          ))}
          <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Counted as the agreement counts them: day one is the day of the event, and what counts is the day it is received.<RulingRef user={user} n={314} why="Day one is the day of the event; timeliness is the day of receipt, shown by its proof." /> The platform decides no question of timeliness.</div>
        </div>
      ) : null}
    </div>
  );
}
